import { useState } from 'react';
import type { Conviction, PositionRole } from '@prm/shared';
import { POSITION_ROLES, knownLeverageFactor, parseImport } from '@prm/shared';

import { Panel } from './Panel';

/** Yahoo's sector vocabulary, plus the two buckets the map adds. */
const SECTOR_OPTIONS = [
  'Technology',
  'Communication Services',
  'Consumer Cyclical',
  'Consumer Defensive',
  'Healthcare',
  'Financial Services',
  'Industrials',
  'Energy',
  'Basic Materials',
  'Real Estate',
  'Utilities',
  'Crypto',
  'Index ETF',
  'Bond ETF',
  'Commodity ETF',
  'Volatility ETF',
];


export interface DraftPosition {
  id: string;
  ticker: string;
  qty: string;
  /** true when `qty` is a dollar notional rather than a share count. */
  notional: boolean;
  costBasis: string;
  role: PositionRole;
  conviction: Conviction;
  /** Tier-3 sector override; blank means fall back to the provider. */
  sectorOverride: string;
  /** Structural leverage as text, so the field can be cleared while editing. */
  leverage: string;
}

interface Props {
  positions: DraftPosition[];
  name: string;
  equity: string;
  dirty: boolean;
  busy: boolean;
  onChange: (positions: DraftPosition[]) => void;
  onNameChange: (name: string) => void;
  onEquityChange: (equity: string) => void;
  onAnalyze: () => void;
}

export function newDraft(over: Partial<DraftPosition> = {}): DraftPosition {
  return {
    id: crypto.randomUUID(),
    ticker: '',
    qty: '',
    notional: false,
    costBasis: '',
    role: 'core',
    conviction: 3,
    sectorOverride: '',
    leverage: '',
    ...over,
  };
}

export function PositionsPanel(props: Props) {
  const { positions, name, equity, dirty, busy, onChange, onNameChange, onEquityChange, onAnalyze } = props;
  const [importing, setImporting] = useState(false);
  const [importText, setImportText] = useState('');
  const [importErrors, setImportErrors] = useState<string[]>([]);

  const update = (id: string, patch: Partial<DraftPosition>) =>
    onChange(positions.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const remove = (id: string) => onChange(positions.filter((p) => p.id !== id));

  const addRow = () => onChange([...positions, newDraft()]);

  const runImport = () => {
    const { positions: parsed, errors } = parseImport(importText);
    setImportErrors(errors.map((e) => `line ${e.line}: ${e.reason}`));
    if (parsed.length === 0) return;

    const imported = parsed.map((p) =>
      newDraft({
        ticker: p.ticker,
        qty: String(p.shares ?? p.value ?? ''),
        notional: p.shares === null,
        costBasis: p.costBasis === null ? '' : String(p.costBasis),
        leverage: knownLeverageFactor(p.ticker) === 1 ? '' : String(knownLeverageFactor(p.ticker)),
      }),
    );

    // Drop the blank starter rows so an import doesn't leave holes behind.
    const kept = positions.filter((p) => p.ticker.trim() !== '');
    onChange([...kept, ...imported]);
    setImportText('');
    setImporting(false);
  };

  return (
    <Panel
      title="Book"
      description="Holdings by share count or dollar notional. Everything downstream reads from here."
      sub={
        <span className="panel__sub">
          {positions.filter((p) => p.ticker.trim() !== '').length} positions
        </span>
      }
      flush
    >
      <div className="book-form">
        <label>
          <span className="form-label">Book name</span>
          <input className="input" value={name} onChange={(e) => onNameChange(e.target.value)} />
        </label>
        <label>
          <span className="form-label">Equity override</span>
          <input
            className="input input--num"
            value={equity}
            placeholder="Auto from holdings"
            inputMode="decimal"
            onChange={(e) => onEquityChange(e.target.value)}
          />
        </label>
        <div className="book-buttons">
          <button className="button" onClick={() => setImporting((v) => !v)}>
            {importing ? 'Cancel import' : 'Paste import'}
          </button>
          <button className="button" onClick={addRow}>
            Add row
          </button>
          <button className="button button--primary" onClick={onAnalyze} disabled={busy}>
            {busy ? 'Running…' : dirty ? 'Save & analyze' : 'Re-analyze'}
          </button>
        </div>
      </div>

      {importing ? (
        <div className="import-box" style={{ display: 'block' }}>
          <textarea
            className="textarea"
            value={importText}
            spellCheck={false}
            placeholder={'AAPL 100\nNVDA, 250, 118.40\nMSFT $25000'}
            onChange={(e) => setImportText(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="button button--primary" onClick={runImport} disabled={importText.trim() === ''}>
              Import pasted rows
            </button>
            <span className="faint" style={{ fontSize: 12.5 }}>
              One per line: TICKER qty [cost basis]. Prefix qty with $ for dollar notional.
            </span>
          </div>
          {importErrors.length > 0 ? (
            <div className="banner banner--warn" style={{ marginTop: 10 }}>
              {importErrors.join(' · ')}
            </div>
          ) : null}
        </div>
      ) : null}

      <datalist id="prm-sectors">
        {SECTOR_OPTIONS.map((sector) => (
          <option key={sector} value={sector} />
        ))}
      </datalist>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 74 }}>Ticker</th>
              <th style={{ width: 96 }}>Qty</th>
              <th style={{ width: 40 }}>$</th>
              <th style={{ width: 84 }}>Cost</th>
              <th style={{ width: 92 }}>Role</th>
              <th style={{ width: 56 }}>Conv</th>
              <th style={{ width: 116 }} title="Overrides the bundled map and the live lookup">Sector</th>
              <th style={{ width: 52 }} title="Structural leverage: 3 for TQQQ, -3 for SQQQ">Lev</th>
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody>
            {positions.length === 0 ? (
              <tr>
                <td colSpan={9} className="empty" style={{ textAlign: 'center' }}>
                  No positions. Add a row or paste-import a book.
                </td>
              </tr>
            ) : null}

            {positions.map((p) => (
              <tr key={p.id}>
                <td>
                  <input
                    className="input input--bare ticker"
                    value={p.ticker}
                    spellCheck={false}
                    placeholder="AAPL"
                    onChange={(e) => {
                      const ticker = e.target.value.toUpperCase();
                      const known = knownLeverageFactor(ticker);
                      // Prefill leverage from the registry, but never clobber a
                      // value the user has already set by hand.
                      update(p.id, {
                        ticker,
                        ...(p.leverage === '' && known !== 1 ? { leverage: String(known) } : {}),
                      });
                    }}
                  />
                </td>
                <td>
                  <input
                    className="input input--bare input--num"
                    value={p.qty}
                    inputMode="decimal"
                    placeholder="0"
                    onChange={(e) => update(p.id, { qty: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={p.notional}
                    title="Quantity is a dollar amount"
                    onChange={(e) => update(p.id, { notional: e.target.checked })}
                  />
                </td>
                <td>
                  <input
                    className="input input--bare input--num"
                    value={p.costBasis}
                    inputMode="decimal"
                    placeholder="—"
                    onChange={(e) => update(p.id, { costBasis: e.target.value })}
                  />
                </td>
                <td>
                  <select
                    className="select"
                    value={p.role}
                    onChange={(e) => update(p.id, { role: e.target.value as PositionRole })}
                  >
                    {POSITION_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className="select"
                    value={p.conviction}
                    onChange={(e) => update(p.id, { conviction: Number(e.target.value) as Conviction })}
                  >
                    {[0, 1, 2, 3, 4, 5].map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    className="input input--bare"
                    value={p.sectorOverride}
                    list="prm-sectors"
                    spellCheck={false}
                    placeholder="auto"
                    title="Leave blank to use the bundled map or live lookup"
                    onChange={(e) => update(p.id, { sectorOverride: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    className="input input--bare input--num"
                    value={p.leverage}
                    inputMode="decimal"
                    placeholder="1"
                    title="Structural leverage. Blank or 1 = ordinary equity."
                    onChange={(e) => update(p.id, { leverage: e.target.value })}
                  />
                </td>
                <td>
                  <button className="button button--sm" onClick={() => remove(p.id)} title="Remove position">
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
